<template>
  <div
    class="portal-active-switch"
    :class="{
      'portal-active-switch--alternate': alternatePortal,
      'portal-active-switch--dark': darkTheme,
    }"
  >
    <div class="text-caption font-weight-bold text-medium-emphasis mb-1">
      Portal activo
    </div>
    <v-autocomplete
      ref="autocomplete"
      :model-value="modelValue"
      v-model:search="search"
      :items="items"
      item-title="title"
      item-value="key"
      :custom-filter="filterItems"
      density="compact"
      variant="outlined"
      color="primary"
      :clearable="hasSearch"
      hide-details
      class="portal-active-switch__autocomplete"
      @click:clear="clearSearch"
      @update:focused="onFocused"
      @update:model-value="onSelected"
    >
      <template #prepend-inner>
        <v-icon :icon="activeIcon" size="20" />
      </template>
      <template #item="{ props, item }">
        <v-list-item
          v-bind="props"
          :prepend-icon="item.raw.icon"
          :subtitle="item.raw.subtitle"
          :active="item.raw.active"
          color="primary"
        />
      </template>
      <template #selection="{ item }">
        <div
          v-if="!focused"
          class="portal-active-switch__selection min-w-0"
        >
          <div class="text-body-2 font-weight-medium text-truncate">
            {{ item.raw.title }}
          </div>
          <div class="text-caption text-medium-emphasis text-truncate">
            {{ item.raw.subtitle }}
          </div>
        </div>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
export default {
  name: "PortalActiveSwitch",
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    activeIcon: {
      type: String,
      default: "",
    },
    alternatePortal: {
      type: Boolean,
      default: false,
    },
    darkTheme: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      search: "",
      focused: false,
    };
  },
  computed: {
    hasSearch() {
      return String(this.search ?? "").trim().length > 0;
    },
  },
  methods: {
    onSelected(key) {
      if (key) this.$emit("update:modelValue", key);
      this.search = "";
      this.focused = false;
      this.$nextTick(() => this.$refs.autocomplete?.blur?.());
    },
    clearSearch() {
      this.search = "";
    },
    onFocused(focused) {
      this.focused = !!focused;
      if (focused) {
        const active = this.items.find((item) => item.key === this.modelValue);
        this.search = active?.title || "";
      } else {
        this.search = "";
      }
    },
    filterItems(_value, query, item) {
      const normalize = (value) =>
        String(value ?? "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLocaleLowerCase()
          .trim();
      const normalizedQuery = normalize(query);
      if (!normalizedQuery) return true;
      const title = normalize(item?.raw?.title ?? item?.title ?? _value);
      return title.includes(normalizedQuery);
    },
  },
};
</script>

<style scoped>
.portal-active-switch {
  width: min(100%, 300px);
}
.portal-active-switch__autocomplete :deep(.v-field) {
  border-radius: 8px;
}
.portal-active-switch__autocomplete :deep(.v-field__input) {
  min-height: 46px;
  padding-top: 4px;
  padding-bottom: 4px;
}
.portal-active-switch__selection {
  line-height: 1.2;
}
.portal-active-switch--alternate
  .portal-active-switch__autocomplete
  :deep(.v-field) {
  --v-field-border-width: 2px;
  box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.12);
}
.portal-active-switch--alternate
  .portal-active-switch__autocomplete
  :deep(.v-field__outline) {
  color: rgb(var(--v-theme-primary));
  --v-field-border-opacity: 1;
}
.portal-active-switch--alternate.portal-active-switch--dark
  .portal-active-switch__autocomplete
  :deep(.v-field) {
  box-shadow: 0 0 0 3px rgba(var(--v-theme-secondary), 0.12);
}
.portal-active-switch--alternate.portal-active-switch--dark
  .portal-active-switch__autocomplete
  :deep(.v-field__outline) {
  color: rgb(var(--v-theme-secondary));
  --v-field-border-opacity: 1;
}
</style>
